import { useTheme } from '@mui/material'
import { useContext, useMemo } from 'react'
import { AxisDomain } from 'recharts/types/util/types'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { ChartValues, DynamicPriceType, PowerPrice } from '../types'
import { checkForNegativePrices, getChartYAxisDomain } from '../utils'
import { transformPowerPriceToHourValues } from '../utils/power-price-widget'

type ChartColorConfig = {
  fill: string
  stroke: string
}

type UsePowerPriceChartConfigProps = {
  to: string
  powerPrices: PowerPrice[]
  currentPowerPriceValues: PowerPrice
  wallboxLimit: number
  batteryLimit: number
  selectedDynamicPowerPriceModalSetting: DynamicPriceType
  isModal?: boolean
  isDynamicEmsEnabled: boolean | undefined
}

type UsePowerPriceChartConfig = {
  chartData: ChartValues[]
  chartColorConfig: ChartColorConfig
  hasNegativePrices: boolean
  yAxisDomain: AxisDomain
  minPrice: number
  maxPrice: number
}

/*
 * a hook that returns configuration values for the dpp widget chart
 */
const usePowerPriceChartConfig = ({
  to,
  powerPrices,
  currentPowerPriceValues,
  wallboxLimit,
  batteryLimit,
  selectedDynamicPowerPriceModalSetting,
  isModal,
  isDynamicEmsEnabled: isDynamicEMSEnabled,
}: UsePowerPriceChartConfigProps): UsePowerPriceChartConfig => {
  const theme = useTheme()
  const { isDynamicPowerPriceEnabledForBattery, isDynamicPowerPriceEnabledForWallboxes } = useContext(DynamicPowerPricesEnabledContext)
  const { chartData, minPrice, maxPrice, hasNegativePrices, yAxisDomain } = useMemo(() => {
    const chartData = transformPowerPriceToHourValues(powerPrices, to)
    const priceValues = powerPrices.map(values => values.price)
    const minPrice = Math.min(...priceValues)
    const maxPrice = Math.max(...priceValues)

    const hasNegativePrices = checkForNegativePrices(powerPrices)
    const yAxisDomain = getChartYAxisDomain(
      minPrice,
      maxPrice,
      wallboxLimit,
      batteryLimit,
      isDynamicPowerPriceEnabledForWallboxes,
      isDynamicPowerPriceEnabledForBattery
    )

    return { chartData, minPrice, maxPrice, hasNegativePrices, yAxisDomain }
  }, [to, powerPrices, wallboxLimit, batteryLimit, isDynamicPowerPriceEnabledForWallboxes, isDynamicPowerPriceEnabledForBattery])

  const { chartColorConfig } = useMemo(() => {
    const redColorConfig = { fill: theme.palette.dppWidget.underTheThresholdColorFill, stroke: theme.palette.dppWidget.underTheThresholdColorStroke }
    const greenColorConfig = { fill: theme.palette.dppWidget.overTheThresholdColorFill, stroke: theme.palette.dppWidget.overTheThresholdColorStroke }
    const dynamicEmsColorConfig = { fill: theme.palette.dppWidget.dynamicEmsColorFill, stroke: theme.palette.dppWidget.dynamicEmsColorStroke }
    const currentPowerPrice = currentPowerPriceValues.price
    let chartIsRed: boolean

    if (isDynamicPowerPriceEnabledForBattery && isDynamicPowerPriceEnabledForWallboxes) {
      if (isModal) {
        chartIsRed =
          (selectedDynamicPowerPriceModalSetting === DynamicPriceType.Wallbox && currentPowerPrice > wallboxLimit) ||
          (selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery && currentPowerPrice > batteryLimit)
      } else {
        chartIsRed = currentPowerPrice > wallboxLimit && currentPowerPrice > batteryLimit
      }
    } else {
      chartIsRed =
        (isDynamicPowerPriceEnabledForWallboxes && currentPowerPrice > wallboxLimit) ||
        (isDynamicPowerPriceEnabledForBattery && currentPowerPrice > batteryLimit)
    }

    if (isDynamicEMSEnabled) {
      if (isModal && selectedDynamicPowerPriceModalSetting === DynamicPriceType.Wallbox)
        return {
          chartColorConfig: currentPowerPrice > wallboxLimit ? redColorConfig : greenColorConfig,
        }
      return {
        chartColorConfig: dynamicEmsColorConfig,
      }
    }

    return {
      chartColorConfig: chartIsRed ? redColorConfig : greenColorConfig,
    }
  }, [
    theme.palette.dppWidget.underTheThresholdColorFill,
    theme.palette.dppWidget.underTheThresholdColorStroke,
    theme.palette.dppWidget.overTheThresholdColorFill,
    theme.palette.dppWidget.overTheThresholdColorStroke,
    theme.palette.dppWidget.dynamicEmsColorFill,
    theme.palette.dppWidget.dynamicEmsColorStroke,
    currentPowerPriceValues.price,
    isDynamicPowerPriceEnabledForBattery,
    isDynamicPowerPriceEnabledForWallboxes,
    isDynamicEMSEnabled,
    isModal,
    selectedDynamicPowerPriceModalSetting,
    wallboxLimit,
    batteryLimit,
  ])

  return {
    chartData,
    chartColorConfig,
    hasNegativePrices,
    yAxisDomain,
    minPrice,
    maxPrice,
  }
}

export default usePowerPriceChartConfig
